import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle, ShieldCheck, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicPay = () => {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [promo, setPromo] = useState('');
  const [applying, setApplying] = useState(false);
  const [paid, setPaid] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/invoices/${token}`);
      setInv(data);
      if (data.status === 'paid') setPaid(true);
    } catch (err) {
      setInv({ _error: err.response?.data?.detail || 'Link no longer valid' });
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // After Stripe redirect, poll payment-status until paid
  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId || paid) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      while (!cancelled && attempts < 15) {
        attempts++;
        try {
          const { data } = await axios.get(`${API}/public/invoices/${token}/payment-status/${sessionId}`);
          if (data.status === 'paid') { setPaid(true); load(); return; }
        } catch { /* keep polling */ }
        await new Promise(r => setTimeout(r, 2000));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params, token, paid, load]);

  const applyCode = async () => {
    if (!promo.trim()) return;
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/public/invoices/${token}/apply-code`, { code: promo.trim().toUpperCase() });
      toast.success(`Code ${data.code} applied — saved $${Number(data.discount_amount || 0).toFixed(2)}`);
      setPromo('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
    setApplying(false);
  };

  const pay = async (payment_type = 'full') => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/public/invoices/${token}/checkout`, {
        origin_url: window.location.origin,
        payment_type,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start payment');
      setPaying(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (inv?._error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-white px-6 text-center">
      <Logo size="md" />
      <p className="text-2xl mt-8">Payment link unavailable</p>
      <p className="text-[#A3A3A3] mt-2">{inv._error}</p>
      <p className="text-[#525252] text-sm mt-6">If you believe this is a mistake, please contact us.</p>
    </div>
  );

  const subtotal = (inv.line_items || []).filter(li => !li.is_discount).reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);
  const due = Number(inv.amount || 0) - Number(inv.amount_paid || 0);

  return (
    <div data-testid="public-pay-page" className="min-h-screen bg-[#0A0A0A] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Logo size="md" showTagline={false} />
          <span className="text-[#525252] text-xs uppercase tracking-[0.22em]">Secure payment</span>
        </div>

        {paid && (
          <div data-testid="pay-success" className="bg-green-500/10 border border-green-500/40 rounded-md p-6 mb-6 flex items-center gap-4">
            <CheckCircle className="w-10 h-10 text-green-400 shrink-0" />
            <div>
              <p className="text-green-400 font-bold text-xl">Payment received</p>
              <p className="text-[#A3A3A3] text-sm">Thank you! A receipt has been recorded on your account.</p>
            </div>
          </div>
        )}

        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
          <div className="p-6 border-b border-[#262626] flex items-start justify-between">
            <div>
              <p className="text-[#D4AF37] text-xs font-bold tracking-[0.18em] uppercase">Invoice {inv.invoice_number}</p>
              <p className="text-white text-lg font-medium mt-1">Hi {inv.customer_name?.split(' ')?.[0] || 'there'},</p>
              <p className="text-[#A3A3A3] text-sm">Here's your invoice from Nexflow Plumbing.</p>
            </div>
            <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded ${paid ? 'bg-green-500/15 text-green-400' : 'bg-[#D4AF37]/15 text-[#D4AF37]'}`}>
              {paid ? 'paid' : (inv.status || 'pending').toUpperCase()}
            </span>
          </div>

          <div className="p-6 space-y-2">
            <p className="text-[#525252] text-[10px] uppercase tracking-wider mb-1">Line items</p>
            {(inv.line_items || []).map((li, i) => (
              <div key={i} className={`flex items-center justify-between text-sm ${li.is_discount ? 'text-green-400' : 'text-white'}`}>
                <span className="truncate flex-1 mr-3">{li.description}{li.quantity > 1 && !li.is_discount ? ` × ${li.quantity}` : ''}</span>
                <span>${(Number(li.quantity || 0) * Number(li.unit_price || 0)).toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t border-[#262626] pt-2 mt-2 flex items-center justify-between text-[#A3A3A3] text-xs">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-white">
              <span className="text-sm">Total</span>
              <span className="text-3xl font-bold text-[#D4AF37]">${Number(inv.amount || 0).toFixed(2)}</span>
            </div>
            {Number(inv.amount_paid || 0) > 0 && (
              <div className="flex items-center justify-between text-[#A3A3A3] text-xs">
                <span>Already paid</span>
                <span>-${Number(inv.amount_paid).toFixed(2)}</span>
              </div>
            )}
            {!paid && due > 0 && (
              <div className="flex items-center justify-between border-t border-[#262626] pt-2 mt-2">
                <span className="text-white text-sm">Amount due now</span>
                <span className="text-xl font-bold text-white">${due.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Promo / referral code */}
          {!paid && (
            <div className="p-6 border-t border-[#262626] bg-[#0A0A0A]/40">
              {inv.promo_code ? (
                <p data-testid="promo-applied" className="text-green-400 text-sm flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Code <span className="font-mono font-bold">{inv.promo_code}</span> applied — {inv.discount_percent || 10}% off
                </p>
              ) : (
                <div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-2 flex items-center gap-2"><Tag className="w-3 h-3" /> Promo / Referral Code</p>
                  <div className="flex gap-2">
                    <Input data-testid="public-promo-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="e.g. NEXF-CODY1" className="bg-[#141414] border-[#262626] text-white uppercase font-mono" />
                    <Button data-testid="public-promo-apply" onClick={applyCode} disabled={applying || !promo.trim()} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                      {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pay button */}
          {!paid && due > 0 && (
            <div className="p-6 border-t border-[#262626]">
              {Number(inv.deposit_amount || 0) > 0 && Number(inv.amount_paid || 0) === 0 && (
                <Button
                  data-testid="pay-deposit-btn"
                  onClick={() => pay('deposit')}
                  disabled={paying}
                  variant="outline"
                  className="w-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] mb-3"
                >
                  {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Pay Deposit Only · ${Number(inv.deposit_amount).toFixed(2)}
                </Button>
              )}
              <Button
                data-testid="pay-full-btn"
                onClick={() => pay('full')}
                disabled={paying}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-12 text-base font-bold"
              >
                {paying ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ShieldCheck className="w-5 h-5 mr-2" />}
                {paying ? 'Redirecting…' : `Pay $${due.toFixed(2)} Securely`}
              </Button>
              <p className="text-[#525252] text-[11px] text-center mt-3">Payments processed by Stripe. We never see your card details.</p>
            </div>
          )}
        </div>

        <p className="text-[#525252] text-xs text-center mt-6">© Nexflow Plumbing · Local · Trusted · Reliable</p>
      </div>
    </div>
  );
};

export default PublicPay;
